import {useMutation, useQuery, useQueryClient} from 'react-query';
import {AxiosRequestConfig} from 'axios';
import {API, ApiEndpointsEnum} from '../api';
import {
  NotificationsReqParams,
  NotificationsListResBody,
  NotificationsResBody,
  MarkNotificationReadReqBody,
  MarkNotificationReadResBody,
  MarkNotificationReadByServiceRequestReqBody,
  MarkNotificationReadByServiceRequestResBody,
} from '../types/notification.types';
import {convertJsonToParams} from '../utils/query.util';

const getNotifications = (params: NotificationsReqParams) => {
  let config: AxiosRequestConfig = {};
  const queryParams = convertJsonToParams(params as any);

  if (params.patientId) {
    const headerAuthorization: any = {
      'X-Subject': params.patientId,
    };

    config = {
      headers: {
        ...headerAuthorization,
      },
    };
  }

  const url = ApiEndpointsEnum.GET_NOTIFICATIONS + '?' + queryParams;
  return API.get<NotificationsListResBody>(url, config);
};

const useGetNotifications = (params: NotificationsReqParams) => {
  return useQuery(['get-notifications', {id: params.patientId}], () =>
    getNotifications(params),
  );
};

const markNotificationRead = async (
  reqBody: MarkNotificationReadReqBody,
) => {
  let config: AxiosRequestConfig = {};

  if (reqBody.subject) {
    config = {
      headers: {
        'X-Subject': reqBody.subject,
      },
    };
  }

  return API.post<MarkNotificationReadResBody>(
    ApiEndpointsEnum.POST_NOTIFICATION_READ,
    {notificationId: reqBody.notificationId},
    config,
  );
};

const markNotificationReadByServiceRequest = async (
  reqBody: MarkNotificationReadByServiceRequestReqBody,
) => {
  let config: AxiosRequestConfig = {};

  if (reqBody.subject) {
    const headerAuthorization: any = {
      'X-Subject': reqBody.subject,
    };

    config = {
      headers: {
        ...headerAuthorization,
      },
    };
  }

  return API.post<MarkNotificationReadByServiceRequestResBody>(
    ApiEndpointsEnum.POST_NOTIFICATION_READ_BY_SERVICE_REQUEST,
    {serviceRequestId: reqBody.serviceRequestId},
    config,
  );
};

const getNotificationsQueryKey = (patientId?: string) => [
  'get-notifications',
  {id: patientId},
];

const invalidateNotifications = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries('get-notifications');
};

const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (reqBody: MarkNotificationReadReqBody) => markNotificationRead(reqBody),
    {
      onSuccess: () => {
        invalidateNotifications(queryClient);
      },
    },
  );
};

const useMarkNotificationReadByServiceRequest = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (reqBody: MarkNotificationReadByServiceRequestReqBody) =>
      markNotificationReadByServiceRequest(reqBody),
    {
      onSuccess: () => {
        invalidateNotifications(queryClient);
        queryClient.invalidateQueries('service-request-count');
      },
    },
  );
};

export {
  getNotifications,
  getNotificationsQueryKey,
  markNotificationRead,
  useGetNotifications,
  useMarkNotificationRead,
  useMarkNotificationReadByServiceRequest,
};
